import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EsignRequestDetail } from "@/pages/esign/widgets/EsignRequestDetail.tsx";
import { EsignRequestList, EsignRequestListRef } from "@/pages/esign/widgets/EsignRequestList.tsx";
import { EsignSignatureCertificateModal } from "@/pages/esign/widgets/EsignSignatureCertificateModal.tsx";
import {
  useEsignEnvelope,
  useEsignEnvelopeCancel,
  useEsignEnvelopesSearch,
} from "@/queries/use-esign.query.ts";
import {
  ApiEsignDocumentEnvelope,
  EsignEnvelopeStatus,
  EsignRequestStatus,
  EsignSignatureType,
} from "@/types/esign.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Button,
  ConfirmationModal,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  useModals,
} from "@skailer-com/uikit";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface EsignEnvelopesModalProps extends ModalProps {
  documentId: number;
  documentTitle: string;
  completedEnvelopesCount: number;
  activeEnvelopesCount: number; // Без cancelled
  totalEnvelopesCount: number; // Все, включая cancelled
}

export function EsignEnvelopesModal({
  documentId,
  documentTitle,
  completedEnvelopesCount,
  activeEnvelopesCount,
  totalEnvelopesCount,
  ...props
}: EsignEnvelopesModalProps) {
  const { t } = useTranslation();
  const { openModal, close } = useModals();
  const { canIEdit } = usePermissions();
  const [showList, setShowList] = useState(false);
  const [selectedEnvelope, setSelectedEnvelope] = useState<ApiEsignDocumentEnvelope | null>(null);
  const listRef = useRef<EsignRequestListRef>(null);
  const { data: envelopes, isLoading } = useEsignEnvelopesSearch({ document_id: documentId });
  const envelopeCancel = useEsignEnvelopeCancel();

  const handleShowCertificate = (envelopeId: string, requestPublicId: string) => {
    openModal(EsignSignatureCertificateModal, { envelopeId, requestPublicId });
    close(props.id);
  };

  const singleEnvelope = envelopes?.metadata.total_items === 1 ? envelopes.items[0] : undefined;
  const detailEnvelope = singleEnvelope ?? selectedEnvelope ?? undefined;

  const { data: detail } = useEsignEnvelope(detailEnvelope?.public_id ?? "", !!detailEnvelope);

  const canCancel =
    canIEdit(ApiPermission.esign_request) &&
    detail?.status === EsignEnvelopeStatus.pending &&
    (detail?.requests.some((request) => request.status === EsignRequestStatus.pending) ?? false);

  const handleCancel = () => {
    if (!detail) return;
    openModal(ConfirmationModal, {
      question: t("core:esign_cancel_request_confirm_title"),
      text: t("core:esign_cancel_request_confirm_text"),
      acceptTitle: t("core:esign_cancel_request_submit"),
      onAccept: async () => {
        try {
          await envelopeCancel.mutateAsync(detail.public_id);
        } catch {
          return;
        }
      },
    });
  };

  const isSignature = detailEnvelope?.signature_type === EsignSignatureType.signature;
  const typeLabel = detailEnvelope
    ? isSignature
      ? t("core:esign_signing")
      : t("workflow:action_type_esign_acknowledgment")
    : undefined;
  const caption = typeLabel ? `${typeLabel} · ${documentTitle}` : documentTitle;

  const onBack = selectedEnvelope
    ? () => setSelectedEnvelope(null)
    : showList
      ? () => setShowList(false)
      : undefined;

  return (
    <Modal
      {...props}
      title={
        detailEnvelope
          ? t("common:request")
          : showList
            ? t("core:esign_envelopes_completed_of", {
                completed: completedEnvelopesCount,
                total: activeEnvelopesCount,
              })
            : t("common:requests")
      }
      caption={caption}
      variant="middle"
      layout="stretch"
      size="md"
      withCloser
      onBack={onBack}
      onModalContentScroll={(scrollTop, scrollHeight, height) =>
        listRef.current?.fetchMoreOnBottomReached(scrollHeight, scrollTop, height)
      }
      actions={
        detailEnvelope && canCancel ? (
          <Button
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={handleCancel}
            isLoading={envelopeCancel.isPending}
          >
            {t("core:esign_cancel_request_submit") + "..."}
          </Button>
        ) : undefined
      }
    >
      <Stack gap="lg">
        {isLoading ? (
          <Stack gap="md">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-2 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </Stack>
        ) : detailEnvelope ? (
          <EsignRequestDetail
            envelope={detailEnvelope}
            documentId={documentId}
            documentTitle={documentTitle}
            onShowCertificate={handleShowCertificate}
          />
        ) : (
          envelopes &&
          totalEnvelopesCount > 0 && (
            <EsignRequestList
              ref={listRef}
              documentId={documentId}
              documentTitle={documentTitle}
              completedEnvelopesCount={completedEnvelopesCount}
              activeEnvelopesCount={activeEnvelopesCount}
              totalEnvelopesCount={totalEnvelopesCount}
              showList={showList}
              onOpenList={() => setShowList(true)}
              onSelectEnvelope={setSelectedEnvelope}
            />
          )
        )}
      </Stack>
    </Modal>
  );
}
